(() => {
  'use strict';
  if (window.LRSMaterialSamples) return;
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionAllowed = () => !reducedMotion.matches && document.body?.dataset.lrsMotion !== 'false';

  // An opt-in runway for soft tops. Page scroll remains native: no wheel or
  // touchmove interception, and no dependency on the Process page's rail.
  function scrollReveal({section, shell, stage, panels, listen, select, fallback}) {
    const clamp = (value, low = 0, high = 1) => Math.max(low, Math.min(high, value));
    const smooth = value => { const x = clamp(value); return x * x * x * (x * (x * 6 - 15) + 10); };
    const y = () => window.scrollY || window.pageYOffset || 0;
    const optedIn = section.dataset.samplesHorizontal === 'true' && !!shell && !section.closest('[data-lrs-process-horizontal]');
    const inset = () => {
      const height = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--lrs-header-height'));
      const header = Number.isFinite(height) ? height : document.querySelector('.site-header')?.getBoundingClientRect().height || 76;
      return header + (document.querySelector('.lrs-vehicle-nav.is-visible')?.getBoundingClientRect().height || 0);
    };
    let enabled = false, frame = 0, measureFrame = 0, start = 0, distance = 1, unit = 1, width = 1;
    let top = 0, viewport = 0, visual = 0, lastTime = 0, destroyed = false;
    let progress = null, fill = null;

    function target() { return clamp((y() - start) / distance); }
    function paint(value) {
      const position = clamp(value) * panels.length;
      const index = Math.min(panels.length - 1, Math.floor(position));
      const fraction = index === panels.length - 1 ? 0 : smooth((position - index - .30) / .70);
      const slide = index + fraction;
      stage.scrollLeft = slide * width;
      select(Math.min(panels.length - 1, Math.round(slide)), true);
      panels.forEach((panel, item) => {
        const exposure = 1 - clamp(Math.abs(slide - item));
        panel.style.setProperty('--sample-reveal-opacity', String(.18 + .82 * smooth(exposure)));
        panel.style.setProperty('--sample-reveal-scale', String(1.025 - .025 * exposure));
      });
      if (fill) fill.style.transform = `scaleX(${clamp(value)})`;
    }
    function tick(time) {
      frame = 0;
      if (!enabled || destroyed) return;
      const next = target();
      const elapsed = lastTime ? Math.min(64, Math.max(1, time - lastTime)) : 16;
      lastTime = time;
      visual += (next - visual) * (1 - Math.exp(-elapsed / 115));
      if (Math.abs(next - visual) < .00015) visual = next;
      paint(visual);
      if (visual !== next) frame = requestAnimationFrame(tick);
    }
    function schedule() {
      if (!enabled) return;
      if (Math.abs(inset() - top) > .5) { queueMeasure(); return; }
      if (!frame) { lastTime = 0; frame = requestAnimationFrame(tick); }
    }
    function clear() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0; lastTime = 0;
      section.classList.remove('is-samples-horizontal');
      ['--samples-runway-height', '--samples-viewport-height', '--samples-pin-top'].forEach(name => section.style.removeProperty(name));
      panels.forEach(panel => {
        panel.style.removeProperty('--sample-reveal-opacity');
        panel.style.removeProperty('--sample-reveal-scale');
        panel.removeAttribute('aria-hidden');
        panel.inert = false;
      });
      stage.scrollLeft = 0;
      progress?.remove(); progress = null; fill = null;
    }
    function measure() {
      measureFrame = 0;
      if (destroyed) return;
      top = inset();
      viewport = Math.max(0, window.innerHeight - top);
      const allowed = optedIn && motionAllowed() && !window.Shopify?.designMode && viewport >= 500;
      if (!allowed) {
        const wasEnabled = enabled;
        enabled = false;
        clear();
        if (wasEnabled) fallback();
        return;
      }
      const wasEnabled = enabled;
      enabled = true;
      section.classList.add('is-samples-horizontal');
      section.style.setProperty('--samples-pin-top', `${top}px`);
      section.style.setProperty('--samples-viewport-height', `${viewport}px`);
      unit = Math.max(440, viewport * .95);
      distance = panels.length * unit;
      section.style.setProperty('--samples-runway-height', `${viewport + distance}px`);
      panels.forEach(panel => { panel.hidden = false; });
      width = Math.max(1, stage.clientWidth);
      // Editor content can grow beyond a phone's available stage. Fall back to
      // the original complete, scrollable tab panel rather than clipping it.
      const copyOverflows = panels.some(panel => {
        const copy = panel.querySelector('[data-sample-copy]');
        return copy && copy.scrollHeight > copy.clientHeight + 2;
      });
      if (copyOverflows) {
        enabled = false;
        clear();
        fallback();
        return;
      }
      start = y() + section.getBoundingClientRect().top - top;
      if (!progress) {
        progress = document.createElement('div');
        progress.className = 'lrs-samples-scroll-progress';
        progress.setAttribute('aria-hidden', 'true');
        fill = document.createElement('span');
        progress.append(fill);
        shell.append(progress);
      }
      visual = target();
      paint(visual);
      if (!wasEnabled) schedule();
    }
    function queueMeasure() { if (!measureFrame && !destroyed) measureFrame = requestAnimationFrame(measure); }
    function go(index) {
      if (!enabled) return false;
      const next = clamp(index, 0, panels.length - 1);
      window.scrollTo({top: Math.max(0, start + next * unit), behavior: motionAllowed() ? 'smooth' : 'auto'});
      return true;
    }
    if (optedIn) {
      listen(window, 'scroll', schedule, {passive: true});
      listen(window, 'resize', queueMeasure, {passive: true});
      if (window.visualViewport) listen(window.visualViewport, 'resize', queueMeasure, {passive: true});
      listen(window, 'pageshow', queueMeasure);
      document.fonts?.ready.then(() => { if (!destroyed) queueMeasure(); });
      queueMeasure();
    }
    return {
      get enabled() { return enabled; }, go, refresh: queueMeasure,
      destroy() {
        destroyed = true; enabled = false;
        if (measureFrame) cancelAnimationFrame(measureFrame);
        measureFrame = 0; clear();
      }
    };
  }

  function init(root = document) {
    const sections = [...root.querySelectorAll('[data-material-samples]')];
    if (root.matches?.('[data-material-samples]')) sections.unshift(root);
    sections.forEach(section => {
      if (instances.has(section)) return;
      const panels = [...section.querySelectorAll('[data-sample]')];
      const tabs = [...section.querySelectorAll('[data-sample-tab]')];
      const tablist = section.querySelector('[data-sample-tabs]');
      const stage = section.querySelector('[data-sample-stage]');
      if (panels.length < 2 || tabs.length !== panels.length || !tablist || !stage) return;

      const abort = new AbortController();
      const listen = (target, name, handler, options = {}) => target.addEventListener(name, handler, {...options, signal: abort.signal});
      let current = 0;
      let horizontal = null;
      let animations = [];
      let pointer = null;
      let suppressImageClickUntil = 0;

      function stopAnimations() {
        animations.forEach(animation => animation.cancel());
        animations = [];
        stage.classList.remove('is-switching');
      }

      function animatePanel(panel, oldHeight) {
        if (!motionAllowed() || typeof stage.animate !== 'function') return;
        const options = {duration: 460, easing: 'cubic-bezier(.22,1,.36,1)'};
        const newHeight = stage.getBoundingClientRect().height;
        const nextAnimations = [];
        if (oldHeight > 0 && newHeight > 0 && Math.abs(oldHeight - newHeight) > 1) {
          stage.classList.add('is-switching');
          nextAnimations.push(stage.animate([{height: `${oldHeight}px`}, {height: `${newHeight}px`}], options));
        }
        const image = panel.querySelector('[data-sample-image]');
        const copy = panel.querySelector('[data-sample-copy]');
        if (image) nextAnimations.push(image.animate([{opacity: 0, transform: 'translateY(12px)'}, {opacity: 1, transform: 'translateY(0)'}], options));
        if (copy) nextAnimations.push(copy.animate([{opacity: 0, transform: 'translateY(9px)'}, {opacity: 1, transform: 'translateY(0)'}], {...options, delay: image ? 50 : 0, fill: 'backwards'}));
        animations = nextAnimations;
        Promise.allSettled(nextAnimations.map(animation => animation.finished)).then(() => {
          if (animations !== nextAnimations) return;
          stage.classList.remove('is-switching');
          animations = [];
        });
      }

      function select(index, keepVisible = false) {
        if (index !== current && panels[current].contains(document.activeElement)) tabs[index].focus({preventScroll: true});
        current = index;
        panels.forEach((panel, panelIndex) => {
          const selected = panelIndex === current;
          panel.hidden = !keepVisible && !selected;
          tabs[panelIndex].setAttribute('aria-selected', String(selected));
          tabs[panelIndex].tabIndex = selected ? 0 : -1;
          if (keepVisible) {
            panel.setAttribute('aria-hidden', String(!selected));
            panel.inert = !selected;
          } else {
            panel.removeAttribute('aria-hidden');
            panel.inert = false;
          }
        });
      }
      function show(index, animate = true) {
        const next = (index + panels.length) % panels.length;
        if (horizontal?.go(next)) return;
        if (next === current && animate) return;
        const oldHeight = stage.getBoundingClientRect().height;
        stopAnimations();
        select(next);
        if (animate) animatePanel(panels[current], oldHeight);
      }

      tablist.setAttribute('role', 'tablist');
      tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        panels[index].setAttribute('role', 'tabpanel');
        panels[index].setAttribute('aria-labelledby', tab.id);
        panels[index].tabIndex = 0;
        listen(tab, 'click', () => show(index));
      });
      section.classList.add('is-enhanced');
      tablist.hidden = false;
      show(0, false);

      listen(tablist, 'keydown', event => {
        const focused = tabs.indexOf(event.target.closest('[data-sample-tab]'));
        if (focused < 0 || event.altKey || event.ctrlKey || event.metaKey) return;
        const destinations = {ArrowRight: focused + 1, ArrowLeft: focused - 1, Home: 0, End: panels.length - 1};
        if (!(event.key in destinations)) return;
        event.preventDefault();
        const next = (destinations[event.key] + panels.length) % panels.length;
        show(next);
        tabs[next].focus({preventScroll: true});
      });

      listen(stage, 'pointerdown', event => {
        if (section.closest('[data-lrs-process-horizontal]')) return;
        if (event.pointerType !== 'touch' || event.isPrimary === false || (event.target.closest('a,button,input,select,textarea') && !event.target.closest('.lrs-detail-trigger'))) return;
        pointer = {id: event.pointerId, x: event.clientX, y: event.clientY};
      }, {passive: true});
      listen(window, 'pointerup', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        pointer = null;
        if (Math.abs(dx) >= 60 && Math.abs(dx) > Math.abs(dy) * 1.7) {
          suppressImageClickUntil = Date.now() + 450;
          show(current + (dx < 0 ? 1 : -1));
        }
      }, {passive: true});
      listen(window, 'pointercancel', () => { pointer = null; }, {passive: true});
      listen(section, 'click', event => {
        if (event.target.closest('.lrs-detail-trigger') && Date.now() < suppressImageClickUntil) { event.preventDefault();event.stopImmediatePropagation(); }
      }, {capture: true});
      listen(reducedMotion, 'change', () => { if (!motionAllowed()) stopAnimations(); horizontal?.refresh(); });
      listen(window, 'resize', stopAnimations, {passive: true});
      listen(section, 'shopify:block:select', event => {
        const index = panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId || panel === event.target.closest('[data-sample]'));
        if (index >= 0) show(index, false);
      });

      horizontal = scrollReveal({section, shell: section.querySelector('[data-sample-shell]'), stage, panels, listen, select, fallback: () => show(current, false)});

      const motionObserver = new MutationObserver(() => { if (!motionAllowed()) stopAnimations(); horizontal?.refresh(); });
      motionObserver.observe(document.body, {attributes: true, attributeFilter: ['data-lrs-motion']});

      instances.set(section, () => {
        abort.abort();
        motionObserver.disconnect();
        horizontal?.destroy();
        stopAnimations();
        section.classList.remove('is-enhanced');
        tablist.hidden = true;
        tablist.removeAttribute('role');
        panels.forEach((panel, index) => {
          panel.hidden = false;
          panel.removeAttribute('role');
          panel.removeAttribute('tabindex');
          panel.setAttribute('aria-labelledby', panel.querySelector('h3').id);
          tabs[index].removeAttribute('role');
          tabs[index].removeAttribute('aria-selected');
          tabs[index].removeAttribute('tabindex');
        });
      });
    });
  }

  function destroy(root) {
    instances.forEach((cleanup, section) => {
      if (root === section || root.contains(section)) { cleanup(); instances.delete(section); }
    });
  }

  window.LRSMaterialSamples = {init, destroy};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
